import React, { useState } from "react";
import axios from "axios";
import { Phone, Send, MapPin, Mail, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Remorquage", "Dépannage", "Gardiennage", "Petit entretien",
  "Nettoyage auto", "Récupération VHU", "Autre",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "",
    service_type: "Remorquage", vehicle: "", message: "", is_urgent: false,
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.location || !form.service_type) {
      toast.error("Merci de remplir nom, téléphone, lieu et service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Demande envoyée ! Je vous rappelle au plus vite.");
      setForm({
        name: "", phone: "", email: "", location: "",
        service_type: "Remorquage", vehicle: "", message: "", is_urgent: false,
      });
    } catch (err) {
      console.error(err);
      toast.error("Erreur d'envoi. Appelez directement le 06 20 73 52 20.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-[#0F0F0F] border-y border-white/5" data-testid="contact-section">
      <div className="absolute inset-0 grid-noise opacity-30" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left info */}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <div className="text-[#FFD600] text-xs font-bold tracking-[0.3em] uppercase mb-3">
                — Contact
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight">
                Une panne ?<br />
                <span className="text-[#FFD600]">Appelez d'abord.</span>
              </h2>
              <p className="mt-5 text-neutral-400 text-lg leading-relaxed">
                Pour une intervention urgente, le téléphone reste le plus rapide.
                Pour les demandes non urgentes, utilisez le formulaire.
              </p>
            </div>

            {/* Big call CTA */}
            <a
              href="tel:+33620735220"
              data-testid="contact-call-cta"
              className="block bg-[#FFD600] hover:bg-[#FACC15] text-[#0A0A0A] rounded-lg p-6 transition-all hover:scale-[1.01] glow-yellow"
            >
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 bg-[#0A0A0A] rounded-md flex items-center justify-center shrink-0">
                  <Phone className="w-6 h-6 text-[#FFD600]" strokeWidth={3} />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.25em] opacity-70">
                    Appel direct · 24/7
                  </div>
                  <div className="font-display text-3xl uppercase tracking-tight">
                    06 20 73 52 20
                  </div>
                </div>
              </div>
            </a>

            <div className="space-y-4">
              <InfoLine icon={MapPin} label="Adresse" value="2670 avenue Julien Panchot, 66000 Perpignan" />
              <InfoLine icon={Clock} label="Horaires" value="24h/24 · 7j/7 · Toute l'année" />
              <InfoLine icon={Mail} label="Email" value="mkremorquage66@gmail.com" />
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-[#0A0A0A] border border-[#262626] rounded-lg p-7 sm:p-10 space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nom *" name="name" value={form.name} onChange={handleChange} testid="input-name" />
              <Field label="Téléphone *" name="phone" type="tel" value={form.phone} onChange={handleChange} testid="input-phone" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} testid="input-email" />
              <Field label="Lieu de l'intervention *" name="location" value={form.location} onChange={handleChange} testid="input-location" placeholder="Adresse, ville…" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-neutral-300 mb-2 block uppercase tracking-wider text-xs">
                  Service *
                </label>
                <select
                  name="service_type"
                  value={form.service_type}
                  onChange={handleChange}
                  data-testid="input-service-type"
                  className="w-full h-12 bg-[#0A0A0A] border border-[#262626] focus:border-[#FFD600] focus:outline-none focus:ring-1 focus:ring-[#FFD600] text-white rounded-md px-4 text-sm"
                >
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <Field label="Véhicule" name="vehicle" value={form.vehicle} onChange={handleChange} testid="input-vehicle" placeholder="Ex: Peugeot 208 essence" />
            </div>
            <div>
              <label className="text-sm font-medium text-neutral-300 mb-2 block uppercase tracking-wider text-xs">
                Message
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                data-testid="input-message"
                rows={4}
                placeholder="Décrivez la panne ou la situation…"
                className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FFD600] focus:outline-none focus:ring-1 focus:ring-[#FFD600] text-white rounded-md px-4 py-3 text-sm resize-none"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer group" data-testid="urgent-toggle">
              <input
                type="checkbox"
                name="is_urgent"
                checked={form.is_urgent}
                onChange={handleChange}
                className="mt-1 h-5 w-5 accent-[#FFD600]"
              />
              <span className="text-sm text-neutral-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#FFD600]" />
                <span>C'est <strong className="text-white">urgent</strong> — préférez l'appel direct</span>
              </span>
            </label>

            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="inline-flex items-center justify-center gap-2 bg-[#FFD600] hover:bg-[#FACC15] disabled:opacity-60 text-[#0A0A0A] font-bold px-8 py-4 rounded-md uppercase tracking-wide text-sm transition-all"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={3} />}
                {loading ? "Envoi…" : "Envoyer la demande"}
              </button>
              <a
                href="tel:+33620735220"
                data-testid="form-call-fallback"
                className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-[#FFD600]/50 text-white font-semibold px-8 py-4 rounded-md uppercase tracking-wide text-sm transition-all"
              >
                <Phone className="w-4 h-4" />
                Ou appeler maintenant
              </a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", value, onChange, testid, placeholder }) => (
  <div>
    <label className="text-sm font-medium text-neutral-300 mb-2 block uppercase tracking-wider text-xs">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full h-12 bg-[#0A0A0A] border border-[#262626] focus:border-[#FFD600] focus:outline-none focus:ring-1 focus:ring-[#FFD600] text-white rounded-md px-4 text-sm placeholder:text-neutral-600"
    />
  </div>
);

const InfoLine = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4 p-4 bg-[#171717] border border-[#262626] rounded-md">
    <div className="h-10 w-10 rounded-md bg-[#FFD600]/10 border border-[#FFD600]/30 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-[#FFD600]" />
    </div>
    <div>
      <div className="text-[10px] text-neutral-500 font-bold uppercase tracking-[0.25em] mb-1">{label}</div>
      <div className="text-white text-sm font-medium">{value}</div>
    </div>
  </div>
);

export default Contact;
